import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Bot, Check, Zap, Copy, CheckCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { promptTemplates } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../components/UI/LoadingSpinner';
import PromptTemplateModal from '../components/PromptTemplate/PromptTemplateModal';
import Avatar from '../components/UI/Avatar';
import HistoryTab from '../components/UI/HistoryTab';

const TEMPLATE_VARS = [
  '{{talent_name}}', '{{company_name}}', '{{company_description}}',
  '{{professions}}', '{{responsibilities}}', '{{tools}}',
  '{{languages}}', '{{experience}}', '{{media}}',
];

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || fallback || '—';
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span className="min-w-0 break-all">{displayName}</span>
    </div>
  );
};

// Highlight {{variables}} in body text
const HighlightedBody = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/({{[^}]+}})/g);
  return (
    <>
      {parts.map((part, i) =>
        TEMPLATE_VARS.includes(part) ? (
          <mark
            key={i}
            className="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded px-0.5 font-medium not-italic"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const CriteriaCard = ({ label, value }) => (
  <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-4 border border-light-border-primary dark:border-dark-border-primary">
    <p className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider mb-2">{label}</p>
    {value ? (
      <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{value}</p>
    ) : (
      <div className="flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Any</p>
      </div>
    )}
  </div>
);

const PromptTemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const canUpdate = hasPermission('talent-service', 'prompt_template', 'update');

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await promptTemplates.getById(id);
      setTemplate(res?.data || res);
    } catch (err) {
      toast.error('Failed to load prompt template');
      navigate('/prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!template?.body) return;
    navigator.clipboard.writeText(template.body).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loader />;
  if (!template) return null;

  const charCount = template.body?.length || 0;
  const lineCount = template.body?.split('\n').length || 0;
  const varCount = TEMPLATE_VARS.filter(v => template.body?.includes(v)).length;

  return (
    <div className="min-h-full bg-light-bg-primary dark:bg-dark-bg-primary">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-light-bg-primary/90 dark:bg-dark-bg-primary/90 backdrop-blur-sm border-b border-light-border-primary dark:border-dark-border-primary">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/prompts')}
              className="p-1.5 rounded-lg text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
            >
              <ArrowLeft size={18} />
            </button>

            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Bot size={14} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-semibold text-light-text-primary dark:text-dark-text-primary leading-tight">
                    {template.name}
                  </h1>
                  {template.is_default && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      <Check size={10} strokeWidth={3} /> Default
                    </span>
                  )}
                </div>
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                  Prompt template · {charCount.toLocaleString()} chars · {lineCount} lines
                  {varCount > 0 && ` · ${varCount} variables`}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary border border-light-border-primary dark:border-dark-border-primary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              {copied ? <CheckCheck size={14} className="text-green-500" /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            {canUpdate && (
              <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-lg shadow-sm transition-all"
              >
                <Edit size={14} />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 border-b border-light-border-primary dark:border-dark-border-primary">
        {['details', 'history'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'history' && (
        <div className="p-6 max-w-5xl mx-auto">
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
            <HistoryTab
              entityId={id}
              fetchEventsFunction={promptTemplates.getEvents}
              fetchStatsFunction={promptTemplates.getEventStats}
            />
          </div>
        </div>
      )}

      {/* Content */}
      {activeTab === 'details' && <><div className="mx-auto max-w-5xl p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

          {/* Main: Prompt Body — takes 2 cols */}
          <div className="min-w-0 space-y-4 lg:col-span-2">
            {/* Body card */}
            <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-secondary border-b border-light-border-primary dark:border-dark-border-primary">
                <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                  Prompt Body
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary font-mono tabular-nums">
                    {charCount.toLocaleString()} chars
                  </span>
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-sm text-light-text-primary dark:text-dark-text-primary leading-relaxed p-5 bg-light-bg-primary dark:bg-dark-bg-primary overflow-x-auto">
                <HighlightedBody text={template.body} />
              </pre>
            </div>
          </div>

          {/* Sidebar — 1 col */}
          <div className="space-y-4">
            {/* Auto-selection */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={13} className="text-purple-500 dark:text-purple-400" />
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                  Auto-match criteria
                </span>
              </div>
              <div className="space-y-2">
                <CriteriaCard
                  label="Platform"
                  value={template.tool?.name || template.tool?.title || (template.tool_id ? `#${template.tool_id}` : null)}
                />
                <CriteriaCard
                  label="Inner Client"
                  value={template.inner_client?.name || (template.inner_client_id ? `#${template.inner_client_id}` : null)}
                />
              </div>
            </div>

            {/* Meta */}
            <div>
              <p className="text-xs font-semibold text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider mb-3">
                Metadata
              </p>
              <div className="space-y-2">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-xl border border-light-border-primary bg-light-bg-secondary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
                  <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created by</span>
                  <div className="min-w-0 max-w-full text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    <UserCell user={template.createdByUser} fallback={template.created_by} />
                  </div>
                </div>
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-xl border border-light-border-primary bg-light-bg-secondary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
                  <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated by</span>
                  <div className="min-w-0 max-w-full text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    <UserCell user={template.updatedByUser} fallback={template.updated_by} />
                  </div>
                </div>
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-xl border border-light-border-primary bg-light-bg-secondary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
                  <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created</span>
                  <span className="min-w-0 break-words text-right text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    {template.created_at ? new Date(template.created_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-xl border border-light-border-primary bg-light-bg-secondary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
                  <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated</span>
                  <span className="min-w-0 break-words text-right text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    {template.updated_at ? new Date(template.updated_at).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      </>}

      <PromptTemplateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        template={template}
        onSaved={() => { setModalOpen(false); fetchTemplate(); }}
      />
    </div>
  );
};

export default PromptTemplateDetail;
